import type { FC, FormEvent } from 'react';
import React, { useContext, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import debounce from 'lodash/debounce';
import { styled } from '@compiled/react';

import SearchIcon from '@atlaskit/icon/core/search';
import Textfield from '@atlaskit/textfield';

import { FiltersContext } from './FiltersContext.tsx';

const i18n = defineMessages({
	searchLabel: {
		id: 'space-directory.filters.search.label',
		defaultMessage: 'Filter by title',
		description: 'Label in the Space directory search box',
	},
	searchIcon: {
		id: 'space-directory.filters.search.icon',
		defaultMessage: 'Search',
		description: 'Accessible label for the search icon in the space directory filter',
	},
	searchDescription: {
		id: 'space-directory.filters.search.aria-describedby.text',
		defaultMessage: 'Spaces will be filtered below as you type',
		description: 'Description of the Space directory search box for screen reader',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScreenReaderOnlyText = styled.span({
	border: 0,
	clip: 'rect(1px, 1px, 1px, 1px)',
	clipPath: 'inset(50%)',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	margin: '-1px',
	overflow: 'hidden',
	padding: 0,
	position: 'absolute',
	width: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	wordWrap: 'normal !important',
});

const SEARCH_FILTER_DESCRIPTION_ID = 'search-filter-description';

export const SearchFilter: FC = () => {
	const intl = useIntl();
	const { filters, updateSearch } = useContext(FiltersContext);
	const debouncedSearch = useMemo(() => debounce(updateSearch, 250), [updateSearch]);
	const onSearchChange = (e: FormEvent<HTMLInputElement>) =>
		debouncedSearch(e.currentTarget.value.trim());

	return (
		<Textfield
			isCompact
			width="medium"
			aria-label={intl.formatMessage(i18n.searchLabel)}
			placeholder={intl.formatMessage(i18n.searchLabel)}
			defaultValue={filters.search}
			id="filter-spaces-by-title"
			onChange={onSearchChange}
			elemBeforeInput={
				<span aria-hidden="true">
					<SearchIcon spacing="spacious" label={intl.formatMessage(i18n.searchIcon)} />
				</span>
			}
			elemAfterInput={
				<ScreenReaderOnlyText id={SEARCH_FILTER_DESCRIPTION_ID} aria-hidden="true">
					{intl.formatMessage(i18n.searchDescription)}
				</ScreenReaderOnlyText>
			}
			aria-describedby={SEARCH_FILTER_DESCRIPTION_ID}
		/>
	);
};
